import {
  Button,
  Card,
  CardBody,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  PriceSegmentType,
  priceSegmentTypeSchema,
} from '@snap-types/models/price-segment';
import type { PriceSegmentConfig } from '@snap-types/models/price-segment-config';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { ErrorMessage } from '@ui/components/form/ErrorMessage';
import { CircleInfoIcon, PlusIcon } from '@ui/components/Icons';
import { EnabledArrayActions } from '@ui/features/snap-site-config/screens/edit/components/ArrayActions';
import { EnabledForm } from '@ui/features/snap-site-config/screens/edit/components/EnabledForm';
import { FormPanel } from '@ui/features/snap-site-config/screens/edit/components/FormPanel';
import { FormPanelHeading } from '@ui/features/snap-site-config/screens/edit/components/FormPanelHeading';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import { LastGrossMarginForm } from '@ui/features/snap-site-config/screens/edit/components/PriceStrategyConfigsForm/price-strategy-config-types/price-segment-types/LastGrossMargin/LastGrossMarginForm';
import { TargetGrossMarginForm } from '@ui/features/snap-site-config/screens/edit/components/PriceStrategyConfigsForm/price-strategy-config-types/TargetGrossMarginForm';
import { ShowWhenEnabled } from '@ui/features/snap-site-config/screens/edit/components/ShowWhenEnabled';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import {
  type FieldArrayPath,
  type FieldPath,
  get,
  useFieldArray,
  type UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { match } from 'ts-pattern';

import { CustomerPortalsForm } from '../price-segment-types/CustomerPortals/CustomerPortalsForm';
import { CustomerPotentialForm } from '../price-segment-types/CustomerPotential/CustomerPotentialForm';
import {
  getAvailablePriceSegmentConfigTypes,
  getDefaultPriceSegmentParamsForType,
  priceSegmentConfigOptions,
} from '../price-segment-types/data';
import { LastTransactionAgeForm } from '../price-segment-types/LastTransactionAge/LastTransactionAgeForm';
import { MarketIndustryForm } from '../price-segment-types/MarketIndustry/MarketIndustryForm';
import { ProductComplexityForm } from '../price-segment-types/ProductComplexity/ProductComplexityForm';
import { ProductOperationsCountForm } from '../price-segment-types/ProductOperationsCount/ProductOperationsCountForm';

export function CAPPriceSegmentationForm({
  fieldPath,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const priceSegmentConfigsFieldPath = fieldPath('priceSegmentConfigs');

  const priceSegmentConfigs = useFieldArray({
    name: priceSegmentConfigsFieldPath as FieldArrayPath<SnapSiteConfigEditFormDataUnvalidated>,
    control: form.control,
  });

  return (
    <FormPanel mt={2}>
      <FormPanelHeading>Price Segments</FormPanelHeading>

      <Flex px={4} direction="column" gap={4}>
        <TargetGrossMarginForm fieldPath={fieldPath} />

        {priceSegmentConfigs.fields.length === 0 ? <EmptyState /> : null}

        {priceSegmentConfigs.fields.map((item, index) => {
          return (
            <FormRow
              id={item.id}
              key={item.id}
              index={index}
              fieldPath={(fieldName) =>
                fieldPath(
                  `priceSegmentConfigs.${index}.${fieldName}`,
                ) as FieldPath<SnapSiteConfigEditFormDataUnvalidated>
              }
              parentFieldPath={priceSegmentConfigsFieldPath}
              fieldActions={priceSegmentConfigs}
            />
          );
        })}

        <ErrorMessage
          name={priceSegmentConfigsFieldPath}
          render={({ message }) => (
            <FormControl isInvalid>
              <FormErrorMessage>{message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <FormRowActions>
          <AddPriceSegmentConfigButton
            priceSegmentConfigsFieldPath={priceSegmentConfigsFieldPath}
            onClick={(type: PriceSegmentType) => {
              priceSegmentConfigs.append(
                getDefaultPriceSegmentParamsForType(type),
              );
            }}
          />
        </FormRowActions>
      </Flex>
    </FormPanel>
  );
}

function EmptyState() {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          No Price Segment Configs set up.
        </Text>

        <Text fontSize="14px">Add a config to get started.</Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Price Segment Config?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Text>
            Price Segment Configs allow you to define how prices are adjusted
            based on customer potential or other criteria. They are useful for
            implementing dynamic pricing strategies that consider customer
            characteristics.
          </Text>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  id,
  onChangeType,
  fieldPath,
  parentFieldPath,
  fieldActions,
  index,
}: {
  id: string;
  onChangeType?: () => void;
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
  parentFieldPath: FieldPath<SnapSiteConfigEditFormDataUnvalidated>;
  fieldActions: UseFieldArrayReturn<
    SnapSiteConfigEditFormDataUnvalidated,
    'priceStrategies'
  >;
  index: number;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const currentSite = useCurrentSite();

  const enabledFieldPath = fieldPath('enabled');
  const priceSegmentConfigTypeFieldPath = fieldPath('priceSegmentType');
  const priceSegmentConfigType = useWatch({
    name: priceSegmentConfigTypeFieldPath,
    control: form.control,
    defaultValue: PriceSegmentType.CustomerPotential,
  }) as PriceSegmentType;

  const priceSegmentConfigTypeField = form.register(
    priceSegmentConfigTypeFieldPath,
  );

  const priceSegmentConfigs = useWatch({
    name: parentFieldPath,
    control: form.control,
  }) as PriceSegmentConfig[];

  const availablePriceSegments = getAvailablePriceSegmentConfigTypes(
    currentSite.code,
    priceSegmentConfigs
      .map((config) => config.priceSegmentType)
      .filter((type) => type !== priceSegmentConfigType),
  );

  return (
    <Card
      variant="outline"
      role="group"
      data-group-scope="price-segment-config"
      sx={{
        viewTransitionName: `price-segment-config-${id}`,
        animationDuration: '0.2s',
        animationTimingFunction: 'cubic-bezier(0.1, 1, 0.3, 1)',
      }}
    >
      <CardBody gap={4} display="flex">
        <Flex flex="1" gap={4} direction="column" justifyContent="center">
          <EnabledForm
            enabledPath={enabledFieldPath}
            otherFieldPath={fieldPath('priceSegmentType')}
            formatValue={(value) => {
              if (value) {
                return priceSegmentConfigOptions[
                  priceSegmentTypeSchema.parse(value)
                ];
              }

              return String(value);
            }}
          />

          <ShowWhenEnabled enabledPath={enabledFieldPath}>
            <FormControl
              isInvalid={
                !!get(form.formState.errors, priceSegmentConfigTypeFieldPath)
              }
              mb={2}
            >
              <FormLabel htmlFor={priceSegmentConfigTypeFieldPath}>
                Price Segment Type:
              </FormLabel>

              <Select
                {...priceSegmentConfigTypeField}
                id={priceSegmentConfigTypeFieldPath}
                onChange={(e) => {
                  onChangeType?.();
                  priceSegmentConfigTypeField.onChange(e);
                }}
              >
                {Object.entries(priceSegmentConfigOptions)
                  .filter(([key]) =>
                    availablePriceSegments.all.includes(
                      key as PriceSegmentType,
                    ),
                  )
                  .map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                      disabled={
                        !availablePriceSegments.available.includes(
                          value as PriceSegmentType,
                        )
                      }
                    >
                      {label}
                    </option>
                  ))}
              </Select>

              <ErrorMessage
                name={priceSegmentConfigTypeFieldPath}
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
            </FormControl>

            {match(priceSegmentConfigType)
              .returnType<React.ReactNode>()
              .with(PriceSegmentType.MarketIndustry, () => (
                <MarketIndustryForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.CustomerPotential, () => (
                <CustomerPotentialForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.CustomerPortals, () => (
                <CustomerPortalsForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.LastGrossMargin, () => (
                <LastGrossMarginForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.LastTransactionAge, () => (
                <LastTransactionAgeForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.ProductComplexity, () => (
                <ProductComplexityForm fieldPath={fieldPath} />
              ))
              .with(PriceSegmentType.ProductOperationsCount, () => (
                <ProductOperationsCountForm fieldPath={fieldPath} />
              ))
              .exhaustive()}
          </ShowWhenEnabled>
        </Flex>

        <EnabledArrayActions
          index={index}
          fieldActions={fieldActions}
          enabledPath={enabledFieldPath}
        />
      </CardBody>
    </Card>
  );
}

function AddPriceSegmentConfigButton({
  priceSegmentConfigsFieldPath,
  onClick,
}: {
  priceSegmentConfigsFieldPath: FieldPath<SnapSiteConfigEditFormDataUnvalidated>;
  onClick: (type: PriceSegmentType) => void;
}) {
  const currentSite = useCurrentSite();
  const form = useFormContext();

  const priceSegmentConfigs = useWatch({
    name: priceSegmentConfigsFieldPath,
    control: form.control,
    defaultValue: [],
  }) as PriceSegmentConfig[];

  const availableSegments = getAvailablePriceSegmentConfigTypes(
    currentSite.id,
    priceSegmentConfigs.map((config) => config.priceSegmentType),
  ).available;

  function handleClick() {
    onClick(availableSegments[0]);
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleClick}
      isDisabled={!availableSegments.length}
      leftIcon={<PlusIcon />}
    >
      Add Price Segment Config
    </Button>
  );
}
